import type { Eval, EvalModel } from '@/types/evals-types';
import {
  CreateEvalV2Request,
  EvalV2Record,
  EvalV2UploadResponse,
  UpdateEvalV2Request,
} from '@/types/eval-v2-types';
import { useOrgStore } from '@/stores/org-store';
import { apiRequest } from '@/utils/api-request';

const orgId = (): string | undefined =>
  useOrgStore.getState().currentOrg?.orgid;

/**
 * List all eval definitions for the org, optionally filtered by workflow.
 * @see platform-api `routes/eval_v2.py` GET /v2/evaluations/{orgid}
 */
export async function listEvalDefinitionsV2(
  params: { workflowId?: string; signal?: AbortSignal } = {},
): Promise<EvalV2Record[]> {
  const id = orgId();
  if (!id) return [];

  const search = new URLSearchParams();
  if (params.workflowId) {
    search.set('workflow_id', params.workflowId);
  }
  const q = search.toString();
  const url = `/v2/evaluations/${id}${q ? `?${q}` : ''}`;

  const response = await apiRequest<EvalV2Record[]>({
    url,
    method: 'GET',
    target: 'platform',
    signal: params.signal,
  });
  if (response.success === false) {
    throw new Error(response.detail || 'Failed to list eval definitions');
  }
  return (response.data as EvalV2Record[]) ?? [];
}

export async function getEvalDefinitionV2(
  evalId: string,
  signal?: AbortSignal,
): Promise<EvalV2Record> {
  const id = orgId();
  if (!id) throw new Error('Organization ID not found');
  const response = await apiRequest<EvalV2Record>({
    url: `/v2/evaluations/${id}/${evalId}`,
    method: 'GET',
    target: 'platform',
    signal,
  });
  if (response.success === false) {
    throw new Error(response.detail || 'Failed to load eval definition');
  }
  return response.data as EvalV2Record;
}

export async function deleteEvalDefinitionV2(evalId: string): Promise<void> {
  const id = orgId();
  if (!id) throw new Error('Organization ID not found');
  const response = await apiRequest({
    url: `/v2/evaluations/${id}/${evalId}`,
    method: 'DELETE',
    target: 'platform',
  });
  if (response.success === false) {
    throw new Error(response.detail || 'Failed to delete eval definition');
  }
}

/** All eval definitions linked to a specific workflow. */
export function listEvalDefinitionsForWorkflow(
  workflowId: string,
  signal?: AbortSignal,
): Promise<EvalV2Record[]> {
  return listEvalDefinitionsV2({ workflowId, signal });
}

export const getEvalsV2 = async (): Promise<EvalV2Record[]> => {
  const id = orgId();
  if (!id) return [];
  const response = await apiRequest<EvalV2Record[]>({
    url: `/v2/evaluations/${id}`,
    method: 'GET',
    target: 'platform',
  });
  if (response.success === false) {
    throw new Error(response.detail || 'Failed to load v2 evals');
  }
  return (response.data as EvalV2Record[]) || [];
};

export const getEvalV2ById = async (evalId: string): Promise<EvalV2Record> => {
  const id = orgId();
  if (!id) throw new Error('Organization ID not found');
  const response = await apiRequest<EvalV2Record>({
    url: `/v2/evaluations/${id}/${evalId}`,
    method: 'GET',
    target: 'platform',
  });
  if (response.success === false) {
    throw new Error(response.detail || 'Failed to load eval record');
  }
  return response.data as EvalV2Record;
};

export const createEvalV2 = async (
  body: CreateEvalV2Request,
): Promise<EvalV2Record> => {
  const id = orgId();
  if (!id) throw new Error('Organization ID not found');
  const response = await apiRequest<EvalV2Record>({
    url: `/v2/evaluations/${id}`,
    method: 'POST',
    body: body as unknown as Record<string, unknown>,
    target: 'platform',
  });
  if (response.success === false) {
    throw new Error(response.detail || 'Failed to create eval record');
  }
  return response.data as EvalV2Record;
};

export const updateEvalV2 = async (
  evalId: string,
  body: UpdateEvalV2Request,
): Promise<EvalV2Record> => {
  const id = orgId();
  if (!id) throw new Error('Organization ID not found');
  const response = await apiRequest<EvalV2Record>({
    url: `/v2/evaluations/${id}/${evalId}`,
    method: 'PATCH',
    body: body as unknown as Record<string, unknown>,
    target: 'platform',
  });
  if (response.success === false) {
    throw new Error(response.detail || 'Failed to update eval record');
  }
  return response.data as EvalV2Record;
};

export const deleteEvalV2 = async (evalId: string): Promise<void> => {
  const id = orgId();
  if (!id) throw new Error('Organization ID not found');
  const response = await apiRequest({
    url: `/v2/evaluations/${id}/${evalId}`,
    method: 'DELETE',
    target: 'platform',
  });
  if (response.success === false) {
    throw new Error(response.detail || 'Failed to delete eval record');
  }
};

export const uploadEvalV2File = async (
  file: File,
): Promise<EvalV2UploadResponse> => {
  const id = orgId();
  if (!id) throw new Error('Organization ID not found');
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiRequest<EvalV2UploadResponse>({
    url: `/v2/evaluations/${id}/upload`,
    method: 'POST',
    body: formData,
    formData: true,
    target: 'platform',
  });
  if (response.success === false) {
    throw new Error(response.detail || 'Failed to upload file');
  }
  return response.data as EvalV2UploadResponse;
};

interface EvalV2DownloadResponse {
  presigned_url: string;
  expires_in: number;
  filename: string;
}

export const downloadEvalV2File = async (
  evalId: string,
): Promise<EvalV2DownloadResponse> => {
  const id = orgId();
  if (!id) throw new Error('Organization ID not found');
  const response = await apiRequest<EvalV2DownloadResponse>({
    url: `/v2/evaluations/${id}/${evalId}/download-url?download=true`,
    method: 'GET',
    target: 'platform',
  });
  if (response.success === false || response.detail || !response.data) {
    throw new Error(response.detail || 'Failed to generate download URL');
  }
  return response.data as EvalV2DownloadResponse;
};

/** True when the eval record came from the v2 table. */
export const isV2Eval = (eval_: Eval): boolean => !!eval_.is_v2;

/** Adapter so v2 eval records can be rendered in the same list as v1 evals. */
export const evalV2ToEval = (e: EvalV2Record): Eval => ({
  id: e.id,
  name: e.name,
  type: e.type,
  description: e.description ?? '',
  model: (e.judge_model as EvalModel | undefined) ?? undefined,
  eval_config: e.eval_config as Eval['eval_config'],
  orgid: e.orgid,
  created_at: e.created_at,
  updated_at: e.updated_at,
  is_v2: true,
});
